/**
 * NetBoxSchedulePanel — Enterprise auto-sync configuration.
 * Allows setting up cron-based automatic NetBox imports per DC.
 */
import { useState, useEffect, useCallback } from 'react'
import { api } from '../../utils/api'
import {
  Clock, Calendar, AlertCircle,
  Loader2, Play, ChevronDown, Info, RefreshCw
} from 'lucide-react'
import toast from 'react-hot-toast'

interface ScheduleConfig {
  configured: boolean
  dc_code?: string
  site_id?: number | null
  enabled?: boolean
  cron_expression?: string
  conflict_strategy?: string
  import_racks?: boolean
  import_devices?: boolean
  import_ports?: boolean
  import_cables?: boolean
  last_synced_at?: string
  last_run_at?: string
  last_run_status?: string
  last_job_id?: number
  next_run_at?: string
}

const PRESETS = [
  { label: 'Каждый час', cron: '0 * * * *' },
  { label: 'Каждые 4 часа', cron: '0 */4 * * *' },
  { label: 'Ежедневно в 02:00 UTC', cron: '0 2 * * *' },
  { label: 'Ежедневно в 06:00 UTC', cron: '0 6 * * *' },
  { label: 'Каждую неделю (пн 02:00)', cron: '0 2 * * 1' },
  { label: 'Свой', cron: 'custom' },
]

const STATUS_COLOR: Record<string, string> = {
  completed: 'text-green-400',
  completed_with_errors: 'text-yellow-400',
  failed: 'text-red-400',
  failed_no_connection: 'text-red-400',
  failed_decrypt_error: 'text-red-400',
}

const STATUS_LABEL: Record<string, string> = {
  completed: 'Успешно',
  completed_with_errors: 'С ошибками',
  failed: 'Ошибка',
  failed_no_connection: 'Нет подключения',
  failed_decrypt_error: 'Ошибка расшифровки',
}

interface Props {
  dcCode: string
  hasSavedConnection: boolean
  defaultSiteId?: number | null
}

export function NetBoxSchedulePanel({ dcCode, hasSavedConnection, defaultSiteId }: Props) {
  const [schedule, setSchedule] = useState<ScheduleConfig | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [triggering, setTriggering] = useState(false)
  const [expanded, setExpanded] = useState(false)

  // Form state
  const [siteId, setSiteId] = useState<number | null>(defaultSiteId ?? null)
  const [enabled, setEnabled] = useState(false)
  const [selectedPreset, setSelectedPreset] = useState('0 2 * * *')
  const [customCron, setCustomCron] = useState('')
  const [conflictStrategy, setConflictStrategy] = useState('netbox_wins')
  const [importRacks, setImportRacks] = useState(true)
  const [importDevices, setImportDevices] = useState(true)
  const [importPorts, setImportPorts] = useState(true)
  const [importCables, setImportCables] = useState(true)

  const isCustom = selectedPreset === 'custom'
  const activeCron = isCustom ? customCron : selectedPreset

  const loadSchedule = useCallback(async () => {
    if (!dcCode) return
    setLoading(true)
    try {
      const res = await api.get(`/api/integrations/netbox/schedule?dc_code=${dcCode}`)
      const s: ScheduleConfig = res.data
      setSchedule(s)
      if (s.site_id !== undefined) {
        setSiteId(s.site_id)
      }
      if (s.configured) {
        setEnabled(s.enabled ?? false)
        const preset = PRESETS.find(p => p.cron === s.cron_expression && p.cron !== 'custom')
        if (preset) {
          setSelectedPreset(s.cron_expression!)
        } else {
          setSelectedPreset('custom')
          setCustomCron(s.cron_expression || '')
        }
        setConflictStrategy(s.conflict_strategy || 'netbox_wins')
        setImportRacks(s.import_racks ?? true)
        setImportDevices(s.import_devices ?? true)
        setImportPorts(s.import_ports ?? true)
        setImportCables(s.import_cables ?? true)
      }
    } catch { /* silent */ }
    finally { setLoading(false) }
  }, [dcCode])

  useEffect(() => {
    if (expanded) loadSchedule()
  }, [expanded, loadSchedule])

  const saveSchedule = async () => {
    if (isCustom && !customCron.trim()) {
      toast.error('Введите cron-выражение')
      return
    }
    setSaving(true)
    try {
      const res = await api.put('/api/integrations/netbox/schedule', {
        dc_code: dcCode,
        site_id: siteId ?? undefined,
        enabled,
        cron_expression: activeCron,
        conflict_strategy: conflictStrategy,
        import_racks: importRacks,
        import_devices: importDevices,
        import_ports: importPorts,
        import_cables: importCables,
      })
      setSchedule({ configured: true, ...res.data })
      toast.success(enabled ? '🕐 Расписание активировано' : 'Расписание сохранено (выключено)')
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Не удалось сохранить расписание')
    } finally {
      setSaving(false)
    }
  }

  const triggerNow = async (fullSync: boolean = false) => {
    setTriggering(true)
    try {
      await api.post(`/api/integrations/netbox/schedule/trigger?dc_code=${dcCode}&full_sync=${fullSync}`)
      toast.success(fullSync ? 'Запущена полная повторная синхронизация' : 'Инкрементальная синхронизация запущена')
      setTimeout(loadSchedule, 3000)
    } catch (e: any) {
      toast.error(e.response?.data?.detail || 'Ошибка запуска')
    } finally {
      setTriggering(false)
    }
  }

  const formatNextRun = (iso?: string) => {
    if (!iso) return 'Не рассчитано'
    const d = new Date(iso + 'Z')
    return d.toLocaleString('ru', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) + ' UTC'
  }

  const formatLastRun = (iso?: string) => {
    if (!iso) return 'Никогда'
    const d = new Date(iso + 'Z')
    const diff = Math.floor((Date.now() - d.getTime()) / 60000)
    if (diff < 1) return 'Только что'
    if (diff < 60) return `${diff} мин. назад`
    const h = Math.floor(diff / 60)
    if (h < 24) return `${h} ч. назад`
    return d.toLocaleDateString('ru')
  }

  return (
    <div className="rounded-lg border border-border bg-bg-panel overflow-hidden">
      {/* Header — always visible */}
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-bg-input/50 transition-colors"
      >
        <div className={`w-2 h-2 rounded-full shrink-0 ${
          schedule?.configured && schedule.enabled ? 'bg-green-400 animate-pulse' :
          schedule?.configured ? 'bg-yellow-400' : 'bg-border'
        }`} />
        <Calendar className="w-4 h-4 text-text-muted shrink-0" />
        <div className="flex-1 text-left">
          <p className="text-sm font-medium text-text-primary">Автоматическая синхронизация</p>
          {schedule?.configured && schedule.enabled ? (
            <p className="text-xs text-text-muted">
              Следующий запуск: {formatNextRun(schedule.next_run_at)}
            </p>
          ) : (
            <p className="text-xs text-text-muted">Настройте расписание для автоматического импорта</p>
          )}
        </div>
        {loading ? (
          <Loader2 className="w-4 h-4 text-text-muted animate-spin" />
        ) : (
          <ChevronDown className={`w-4 h-4 text-text-muted transition-transform ${expanded ? 'rotate-180' : ''}`} />
        )}
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-border p-4 space-y-5">
          {!hasSavedConnection && (
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-yellow-500/10 border border-yellow-500/30">
              <AlertCircle className="w-4 h-4 text-yellow-400 shrink-0" />
              <p className="text-xs text-yellow-400">
                Сначала сохраните подключение к NetBox (шаг 1) для использования автосинхронизации.
              </p>
            </div>
          )}

          {/* Last run status & last synced */}
          <div className="space-y-2">
            {schedule?.last_synced_at && (
              <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-bg-input/50 border border-border">
                <Clock className="w-3.5 h-3.5 text-accent-primary shrink-0" />
                <div className="flex-1 text-xs">
                  <span className="text-text-muted">Последняя успешная синхронизация: </span>
                  <span className="text-text-secondary font-medium">{formatLastRun(schedule.last_synced_at)}</span>
                </div>
              </div>
            )}
            {schedule?.configured && schedule.last_run_at && (
              <div className="flex items-center gap-3 px-3 py-2 rounded-lg bg-bg-input/50 border border-border">
                <Clock className="w-3.5 h-3.5 text-text-muted shrink-0" />
                <div className="flex-1 text-xs">
                  <span className="text-text-muted">Последний запуск: </span>
                  <span className="text-text-secondary">{formatLastRun(schedule.last_run_at)}</span>
                </div>
                {schedule.last_run_status && (
                  <span className={`text-xs font-medium ${STATUS_COLOR[schedule.last_run_status] || 'text-text-muted'}`}>
                    {STATUS_LABEL[schedule.last_run_status] || schedule.last_run_status}
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Enable toggle */}
          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={enabled}
              onChange={e => setEnabled(e.target.checked)}
              className="sr-only"
            />
            <div
              className={`relative w-11 h-6 rounded-full transition-colors cursor-pointer ${
                enabled ? 'bg-accent-primary' : 'bg-bg-input border border-border'
              }`}
            >
              <div className={`absolute top-1 w-4 h-4 rounded-full bg-white shadow transition-all ${
                enabled ? 'left-6' : 'left-1'
              }`} />
            </div>
            <div>
              <p className="text-sm font-medium text-text-primary">
                {enabled ? 'Автосинхронизация включена' : 'Автосинхронизация выключена'}
              </p>
              <p className="text-xs text-text-muted">
                {enabled ? 'Импорт будет запускаться по расписанию автоматически' : 'Включите для автоматического импорта'}
              </p>
            </div>
          </label>

          {/* NetBox Site ID */}
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-2 uppercase tracking-wider">
              NetBox Site ID <span className="normal-case text-text-muted font-normal">(опционально)</span>
            </label>
            <input
              type="number"
              min={1}
              value={siteId ?? ''}
              onChange={e => setSiteId(e.target.value ? Number(e.target.value) : null)}
              placeholder="Все сайты"
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-lg text-sm text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent-primary"
            />
            <p className="text-[11px] text-text-muted mt-1">
              Ограничить синхронизацию одним сайтом NetBox. Оставьте пустым, чтобы синхронизировать все сайты.
            </p>
          </div>

          {/* Schedule presets */}
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-2 uppercase tracking-wider">
              Расписание
            </label>
            <div className="grid grid-cols-2 gap-2">
              {PRESETS.map(p => (
                <button
                  key={p.cron}
                  onClick={() => setSelectedPreset(p.cron)}
                  className={`py-2 px-3 rounded-lg text-xs font-medium text-left transition-all border ${
                    selectedPreset === p.cron
                      ? 'border-accent-primary bg-accent-primary/10 text-accent-primary'
                      : 'border-border bg-bg-input text-text-secondary hover:border-accent-primary/40'
                  }`}
                >
                  {p.label}
                  {p.cron !== 'custom' && (
                    <span className="block text-[10px] font-mono opacity-60 mt-0.5">{p.cron}</span>
                  )}
                </button>
              ))}
            </div>

            {isCustom && (
              <div className="mt-2">
                <input
                  type="text"
                  value={customCron}
                  onChange={e => setCustomCron(e.target.value)}
                  placeholder="* * * * *  (минута час день месяц день_недели)"
                  className="w-full px-3 py-2 bg-bg-input border border-border rounded-lg text-sm font-mono text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent-primary"
                />
                <p className="text-[11px] text-text-muted mt-1">Формат UTC. Пример: 0 2 * * * = каждый день в 02:00 UTC</p>
              </div>
            )}
          </div>

          {/* Import options */}
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-2 uppercase tracking-wider">
              Что импортировать
            </label>
            <div className="grid grid-cols-2 gap-2">
              {[
                { label: 'Стойки', value: importRacks, onChange: setImportRacks },
                { label: 'Устройства', value: importDevices, onChange: setImportDevices },
                { label: 'Порты', value: importPorts, onChange: setImportPorts },
                { label: 'Кабели', value: importCables, onChange: setImportCables },
              ].map(item => (
                <label key={item.label} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={item.value}
                    onChange={e => item.onChange(e.target.checked)}
                    className="w-3.5 h-3.5 rounded border-border accent-accent-primary"
                  />
                  <span className="text-xs text-text-secondary">{item.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Conflict strategy */}
          <div>
            <label className="block text-xs font-medium text-text-secondary mb-2 uppercase tracking-wider">
              Стратегия конфликтов
            </label>
            <select
              value={conflictStrategy}
              onChange={e => setConflictStrategy(e.target.value)}
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-lg text-sm text-text-primary focus:outline-none focus:border-accent-primary"
            >
              <option value="netbox_wins">NetBox приоритет (перезаписывать локальные)</option>
              <option value="local_wins">Локальный приоритет (сохранять локальные)</option>
              <option value="manual">Ручное разрешение (пропускать конфликты)</option>
            </select>
          </div>

          {/* Info */}
          <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-500/5 border border-blue-500/20">
            <Info className="w-3.5 h-3.5 text-blue-400 shrink-0 mt-0.5" />
            <p className="text-xs text-text-muted">
              Автосинхронизация использует сохранённые настройки подключения.
              Все запуски записываются в историю импортов. Время указывается в UTC.
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={saveSchedule}
              disabled={saving || !dcCode || (isCustom && !customCron.trim())}
              className="flex-1 min-w-[170px] flex items-center justify-center gap-2 py-2.5 px-4 bg-accent-primary hover:bg-accent-primary/90 disabled:opacity-50 text-white rounded-lg text-sm font-medium transition-all"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Calendar className="w-4 h-4" />}
              {saving ? 'Сохраняем...' : 'Сохранить расписание'}
            </button>

            {hasSavedConnection && (
              <>
                <button
                  onClick={() => triggerNow(false)}
                  disabled={triggering}
                  className="flex items-center gap-1.5 py-2.5 px-3 border border-border hover:border-green-500/50 bg-bg-panel text-text-secondary hover:text-green-400 rounded-lg text-sm transition-all disabled:opacity-50"
                  title="Запустить дельта-синхронизацию (только изменения)"
                >
                  {triggering ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
                  <span className="text-xs">Дельта-синк</span>
                </button>
                <button
                  onClick={() => triggerNow(true)}
                  disabled={triggering}
                  className="flex items-center gap-1.5 py-2.5 px-3 border border-border hover:border-blue-500/50 bg-bg-panel text-text-secondary hover:text-blue-400 rounded-lg text-sm transition-all disabled:opacity-50"
                  title="Полная повторная синхронизация (без дельта-фильтра)"
                >
                  <RefreshCw className={`w-4 h-4 ${triggering ? 'animate-spin' : ''}`} />
                  <span className="text-xs">Полная повторная синхронизация</span>
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
